<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { ButtonComponent, FlexComponent, ThumbnailOnlyBadge } from '../../..'
import ArrowIcon from '../../../../assets/icons/arrow.svg?component'
import PlayIcon from '../../../../assets/icons/play.svg?component'
import { isThumbnailOnlyVideo, resolveVideoPreload } from '../../../../utils'
import type { ContentItem } from '../content-item-details.types'
const props = defineProps<{
  activeContentItem: ContentItem
  contentItems: ContentItem[]
  activeAssetIndex: number
  canPrevAsset: boolean
  canNextAsset: boolean
}>()
const emits = defineEmits<{
  (event: 'prevAsset'): void
  (event: 'nextAsset'): void
}>()
const activeAsset = computed(() => props.activeContentItem.assets[0] ?? null)
const isThumbnailOnly = computed(() => Boolean(activeAsset.value && isThumbnailOnlyVideo(activeAsset.value)))
const isVideoPlaying = ref(false)
watch(
  () => props.activeContentItem,
  () => {
    isVideoPlaying.value = false
  },
)
const toggleVideo = (event: MouseEvent) => {
  const video = (event.currentTarget as HTMLElement).parentElement?.querySelector('video')
  if (!video) {
    return
  }
  if (video.paused) {
    void video.play()
    isVideoPlaying.value = true
  } else {
    video.pause()
    isVideoPlaying.value = false
  }
}
</script>
<template>
  <div class="content-item-details__media">
    <img
      v-if="activeAsset && activeAsset.type === 'image'"
      :src="activeAsset.url"
      :alt="activeContentItem.profileHandle"
      class="content-item-details__media-el"
    />
    <div v-else-if="activeAsset && isThumbnailOnly" class="content-item-details__video">
      <img
        v-if="activeAsset.thumbnailUrl"
        :src="activeAsset.thumbnailUrl"
        :alt="activeContentItem.profileHandle"
        class="content-item-details__media-el"
      />
      <ThumbnailOnlyBadge class="content-item-details__thumbnail-only-badge" />
    </div>
    <div v-else-if="activeAsset" class="content-item-details__video">
      <video
        :src="activeAsset.url"
        :poster="activeAsset.thumbnailUrl"
        class="content-item-details__media-el"
        :controls="isVideoPlaying"
        :preload="resolveVideoPreload(activeAsset)"
        @click.stop
      />
      <ButtonComponent
        v-if="!isVideoPlaying"
        class="content-item-details__play"
        variant="transparent"
        is-icon
        aria-label="Play video"
        @click.stop="toggleVideo"
      >
        <PlayIcon />
      </ButtonComponent>
    </div>
    <ButtonComponent
      v-if="canPrevAsset"
      class="content-item-details__media-nav content-item-details__media-nav--prev"
      variant="transparent"
      is-icon
      aria-label="Previous media"
      @click="emits('prevAsset')"
    >
      <ArrowIcon />
    </ButtonComponent>
    <ButtonComponent
      v-if="canNextAsset"
      class="content-item-details__media-nav content-item-details__media-nav--next"
      variant="transparent"
      is-icon
      aria-label="Next media"
      @click="emits('nextAsset')"
    >
      <ArrowIcon style="transform: rotate(180deg)" />
    </ButtonComponent>
    <FlexComponent v-if="contentItems.length > 1" class="content-item-details__dots" align="center" justify="center" :gap="4">
      <span
        v-for="(item, index) in contentItems"
        :key="item.id"
        class="content-item-details__dot"
        :class="{ 'content-item-details__dot--active': index === activeAssetIndex }"
      />
    </FlexComponent>
  </div>
</template>
<style scoped lang="scss">
.content-item-details__media {
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--cid-media-width);
  height: 100%;
  background: var(--button-hover);
  overflow: hidden;
  @media screen and (max-width: 991px) {
    width: 100%;
    height: 45vh;
  }
}
.content-item-details__media-el {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.content-item-details__video {
  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;
}
.content-item-details__play {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  backdrop-filter: blur(4px);
  z-index: 2;
}
.content-item-details__thumbnail-only-badge {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
}
.content-item-details__media-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(255, 255, 255, 0.64);
  border-radius: 40px;
  &:hover {
    background-color: rgba(255, 255, 255, 0.84);
  }
  &--prev {
    left: 12px;
  }
  &--next {
    right: 12px;
  }
}
.content-item-details__dots {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
}
.content-item-details__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: var(--secondary-48);
  transition: all var(--transition) ease;
  &--active {
    width: 20px;
    border-radius: 4px;
    background-color: var(--primary);
  }
}
</style>
